import { useMemo, useRef, type ReactNode } from 'react'
import { useFrame } from '@react-three/fiber'
import { Euler, Group, Quaternion } from 'three'
import { Handle, HandleStore } from '@react-three/handle'
import { RigidBody, CuboidCollider, type RapierRigidBody } from '@react-three/rapier'
import { MAX_SCALE, MIN_SCALE } from './logic'
import { anyXRPressed, inXRSession } from './pressTracking'
import { useBounds } from '../../scene/bounds'
import type { Quat, Vec3 } from '../../scene/spawn'

// Rapier RigidBodyType values (the enum is WASM-gated and undefined at import time).
const DYNAMIC = 0
const KINEMATIC = 2 // KinematicPositionBased

// Frames an orphaned grab may persist before the watchdog cancels it (covers the brief
// event-order window where a grab starts before selectstart increments the press counter).
const ORPHAN_GRACE_FRAMES = 4

type AxisToggle = boolean | { x?: boolean; y?: boolean; z?: boolean }

export interface GrabbableTransform {
  position: Vec3
  quaternion: Quat
  scale: number
}

export interface GrabbableProps {
  children: ReactNode
  assetId: string
  position?: Vec3
  quaternion?: Quat
  scale?: number
  physics?: boolean
  translate?: boolean
  rotate?: AxisToggle
  uniformScale?: boolean
  minScale?: number
  maxScale?: number
  multitouch?: boolean
  onRelease?(t: GrabbableTransform): void
}

const range = (min: number, max: number) => [min, max] as [number, number]

// A grabbable model lives in a RigidBody so it can opt into physics per-instance. Physics off
// (default): the body stays kinematicPosition — it floats wherever placed and never falls. Physics
// on: idle body is dynamic (gravity + collides with the floor and other physics bodies). Grabbing
// always switches to kinematicPosition driven from the hand; release restores the idle type, and
// Rapier derives a throw from the kinematic motion. The Handle drives a world-space proxy with its
// default apply (axis-locked rotation + scale clamp); useFrame pushes proxy->body while grabbed and
// pulls body->proxy while idle so the next grab starts without a jump. The collider is the model's
// actual bounding box (published by Model) scaled by the instance scale, only present under physics.
export function Grabbable({
  children,
  assetId,
  position,
  quaternion,
  scale = 1,
  physics = false,
  translate = true,
  rotate = { y: true },
  uniformScale = true,
  minScale = MIN_SCALE,
  maxScale = MAX_SCALE,
  multitouch = true,
  onRelease,
}: GrabbableProps) {
  const proxy = useRef<Group>(null)
  const visual = useRef<Group>(null)
  const body = useRef<RapierRigidBody>(null)
  const store = useRef<HandleStore<unknown>>(null)
  const wasGrabbed = useRef(false)
  const orphanFrames = useRef(0)
  const scaleRef = useRef(scale)

  const box = useBounds((s) => s.byAsset[assetId])

  const rotation = useMemo<[number, number, number] | undefined>(() => {
    if (!quaternion) return undefined
    const e = new Euler().setFromQuaternion(new Quaternion().fromArray(quaternion))
    return [e.x, e.y, e.z]
  }, [quaternion])

  // Half-extents of the model box at the current scale; `args` changing recreates the collider
  // (it's an immutable collider option in @react-three/rapier), so this resizes on scale change.
  const half = useMemo<[number, number, number] | undefined>(
    () => (box ? [(box[0] / 2) * scale, (box[1] / 2) * scale, (box[2] / 2) * scale] : undefined),
    [box, scale],
  )

  useFrame(() => {
    const b = body.current
    const p = proxy.current
    if (!b || !p) return

    const grabbed = store.current?.getState() != null

    // Watchdog: a dropped pointerup leaves the grab active with nothing pressed — cancel it so the
    // object stops sticking to the controller. Only in a live XR session (desktop uses the mouse).
    if (grabbed && inXRSession() && !anyXRPressed()) {
      if (++orphanFrames.current > ORPHAN_GRACE_FRAMES) {
        orphanFrames.current = 0
        store.current?.cancel()
        return
      }
    } else {
      orphanFrames.current = 0
    }

    if (!grabbed && wasGrabbed.current) {
      onRelease?.({
        position: [p.position.x, p.position.y, p.position.z],
        quaternion: [p.quaternion.x, p.quaternion.y, p.quaternion.z, p.quaternion.w],
        scale: scaleRef.current,
      })
    }
    wasGrabbed.current = grabbed

    if (grabbed) {
      if (b.bodyType() !== KINEMATIC) b.setBodyType(KINEMATIC, true)
      scaleRef.current = p.scale.x
      b.setNextKinematicTranslation({ x: p.position.x, y: p.position.y, z: p.position.z })
      b.setNextKinematicRotation({
        x: p.quaternion.x,
        y: p.quaternion.y,
        z: p.quaternion.z,
        w: p.quaternion.w,
      })
    } else {
      // Idle type tracks the physics flag (handles release and live toggles from the edit menu).
      const idleType = physics ? DYNAMIC : KINEMATIC
      if (b.bodyType() !== idleType) b.setBodyType(idleType, true)
      const t = b.translation()
      const r = b.rotation()
      p.position.set(t.x, t.y, t.z)
      p.quaternion.set(r.x, r.y, r.z, r.w)
    }
    visual.current?.scale.setScalar(scaleRef.current)
  })

  return (
    <>
      <group ref={proxy} position={position} rotation={rotation} scale={scale} />
      <RigidBody
        ref={body}
        type={physics ? 'dynamic' : 'kinematicPosition'}
        colliders={false}
        position={position}
        rotation={rotation}
        linearDamping={0.5}
        angularDamping={0.5}
        restitution={0.1}
      >
        {physics && half && <CuboidCollider args={half} />}
        <Handle
          ref={store}
          targetRef={proxy}
          translate={translate}
          rotate={rotate}
          scale={{ uniform: uniformScale, x: range(minScale, maxScale) }}
          multitouch={multitouch}
        >
          <group ref={visual}>{children}</group>
        </Handle>
      </RigidBody>
    </>
  )
}
